import React, { useState, useEffect } from 'react';
import { Save, Download, Upload, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';
import { downloadResumeCsv, downloadResumeJson } from '../utils/resumeExport';

/**
 * ProfileAutoSave Component
 * Provides:
 * - A) Auto-save to AWS
 * - B) Persistent local storage
 * - C) Backup/restore functionality
 */
const ProfileAutoSave = ({ profileData, onRestore }) => {
  const { user } = useAuth();
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, success, error
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [message, setMessage] = useState('');

  // Auto-save timer
  useEffect(() => {
    if (!autoSaveEnabled || !user || !profileData) return;

    const timer = setTimeout(() => {
      handleAutoSave();
    }, 3000); // Auto-save 3 seconds after last change

    return () => clearTimeout(timer);
  }, [profileData, autoSaveEnabled, user]);

  // A) AUTO-SAVE TO AWS
  const handleAutoSave = async () => {
    if (!user?.user_id || !profileData) return;
    
    // Need a resume ID to update
    const resumeId = profileData?.id || profileData?._id;
    if (!resumeId) {
      console.warn('ProfileAutoSave: No resume ID found, skipping auto-save');
      return;
    }

    try {
      setSaveStatus('saving');
      setMessage('Saving to AWS...');

      // Save to AWS DynamoDB using the update method
      await resumeAPI.update(resumeId, profileData);

      // B) ALSO SAVE TO LOCAL STORAGE (persistent)
      const persistKey = `profile_backup_${user.email}`;
      localStorage.setItem(persistKey, JSON.stringify({
        timestamp: Date.now(),
        profile: profileData,
        user: {
          user_id: user.user_id,
          email: user.email,
          name: user.name
        }
      }));

      setSaveStatus('success');
      setLastSaved(new Date());
      setMessage('✅ Saved to AWS & Local');

      setTimeout(() => {
        setSaveStatus('idle');
        setMessage('');
      }, 3000);

    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
      setMessage('❌ Save failed: ' + error.message);
    }
  };

  // Manual save button
  const handleManualSave = async () => {
    await handleAutoSave();
  };

  const handleDownloadJson = () => {
    if (!profileData) return;
    downloadResumeJson(profileData, `${profileData.title || 'profile'}.json`);
    setMessage('✅ JSON downloaded!');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDownloadCsv = () => {
    if (!profileData) return;
    downloadResumeCsv(profileData, `${profileData.title || 'profile'}.csv`);
    setMessage('✅ CSV downloaded!');
    setTimeout(() => setMessage(''), 3000);
  };

  // C) RESTORE FROM FILE (Upload)
  const handleRestoreFromFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        
        if (!backup.profile) {
          throw new Error('Invalid backup file format');
        }

        // Restore profile data
        if (onRestore) {
          onRestore(backup.profile);
        }

        setMessage('✅ Profile restored from backup!');
        setTimeout(() => setMessage(''), 3000);

      } catch (error) {
        console.error('Restore error:', error);
        setMessage('❌ Failed to restore: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  // Restore from local storage
  const handleRestoreFromLocal = () => {
    if (!user?.email) return;

    const persistKey = `profile_backup_${user.email}`;
    const backupStr = localStorage.getItem(persistKey);

    if (!backupStr) {
      setMessage('❌ No local backup found');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const backup = JSON.parse(backupStr);
      
      if (onRestore && backup.profile) {
        onRestore(backup.profile);
        setMessage('✅ Restored from local backup!');
      }

      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Restore error:', error);
      setMessage('❌ Failed to restore: ' + error.message);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Save className="w-5 h-5 text-primary-600" />
          Profile Auto-Save & Backup
        </h3>
        
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoSaveEnabled}
              onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              className="rounded text-primary-600"
            />
            Auto-save
          </label>
        </div>
      </div>

      {/* Status Display */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          saveStatus === 'error' 
            ? 'bg-red-50 text-red-700' 
            : 'bg-green-50 text-green-700'
        }`}>
          {saveStatus === 'saving' && <Clock className="w-4 h-4 animate-spin" />}
          {saveStatus === 'success' && <CheckCircle className="w-4 h-4" />}
          {saveStatus === 'error' && <AlertCircle className="w-4 h-4" />}
          <span className="text-sm">{message}</span>
        </div>
      )}

      {lastSaved && (
        <div className="mb-4 text-xs text-gray-500">
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* A) Manual Save to AWS */}
        <button
          onClick={handleManualSave}
          disabled={saveStatus === 'saving'}
          className="btn btn-primary flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4" />
          Save Now
        </button>

        {/* JSON Download */}
        <button
          onClick={handleDownloadJson}
          className="btn btn-outline flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Download JSON
        </button>

        {/* CSV Download */}
        <button
          onClick={handleDownloadCsv}
          className="btn btn-outline flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Download CSV
        </button>
      </div>

      {/* Info Text */}
      <div className="mt-4 text-xs text-gray-500 space-y-1">
        <p>• <strong>Auto-save:</strong> Saves to AWS every 3 seconds after changes</p>
        <p>• <strong>Local backup:</strong> Persists even after logout</p>
        <p>• <strong>Download:</strong> Export full profile data as JSON or CSV</p>
      </div>
    </div>
  );
};

export default ProfileAutoSave;
